import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Table } from 'react-bootstrap';
import { collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { v4 as uuidv4 } from 'uuid';

const SeminarForm = ({ seminar, show, onHide }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tugasAkhirList, setTugasAkhirList] = useState([]);
  const [dosenList, setDosenList] = useState([]);

  const [formData, setFormData] = useState({
    jenis: 'proposal',
    tugasAkhirId: '',
    tanggal: '',
    waktu: '',
    tempat: '',
    pengujiId: [],
    catatan: '',
    isReschedule: false,
    previousSeminarId: null,
    kriteriaPenilaian: [
      { id: uuidv4(), nama: 'Presentasi', bobot: 20 },
      { id: uuidv4(), nama: 'Penguasaan Materi', bobot: 30 },
      { id: uuidv4(), nama: 'Metodologi', bobot: 25 },
      { id: uuidv4(), nama: 'Dokumentasi', bobot: 25 }
    ]
  });

  const [selectedPenguji, setSelectedPenguji] = useState([]);
  const [selectedTA, setSelectedTA] = useState(null);

  useEffect(() => {
    if (show) {
      fetchData();

      // Jika mode edit, isi form dengan data seminar
      if (seminar) {
        setFormData({
          jenis: seminar.jenis || 'proposal',
          tugasAkhirId: seminar.tugasAkhirId || '',
          tanggal: seminar.tanggal ? new Date(seminar.tanggal).toISOString().split('T')[0] : '',
          waktu: seminar.waktu || '',
          tempat: seminar.tempat || '',
          pengujiId: seminar.pengujiId || [],
          catatan: seminar.catatan || '',
          isReschedule: seminar.isReschedule || false,
          previousSeminarId: seminar.previousSeminarId || null,
          kriteriaPenilaian: seminar.kriteriaPenilaian || [
            { id: uuidv4(), nama: 'Presentasi', bobot: 20 },
            { id: uuidv4(), nama: 'Penguasaan Materi', bobot: 30 },
            { id: uuidv4(), nama: 'Metodologi', bobot: 25 },
            { id: uuidv4(), nama: 'Dokumentasi', bobot: 25 }
          ]
        });

        setSelectedPenguji(seminar.pengujiId || []);
      }
    }
  }, [show, seminar]);

  useEffect(() => {
    // Ketika tugasAkhirId berubah, ambil detail TA
    if (formData.tugasAkhirId) {
      fetchTADetail(formData.tugasAkhirId);
    }
  }, [formData.tugasAkhirId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Ambil daftar tugas akhir yang sudah disetujui
      const taQuery = query(
        collection(db, "tugasAkhir"),
        where("status", "==", "approved")
      );
      const taSnapshot = await getDocs(taQuery);

      const taData = [];
      for (const docSnapshot of taSnapshot.docs) {
        const ta = {
          id: docSnapshot.id,
          ...docSnapshot.data()
        };

        // Ambil data mahasiswa
        if (ta.mahasiswaId) {
          try {
            const mahasiswaDocRef = doc(db, "users", ta.mahasiswaId);
            const mahasiswaDoc = await getDoc(mahasiswaDocRef);
            if (mahasiswaDoc.exists()) {
              ta.mahasiswa = mahasiswaDoc.data();
            }
          } catch (error) {
            console.error("Error fetching mahasiswa data:", error);
          }
        }

        taData.push(ta);
      }

      setTugasAkhirList(taData);

      // Ambil daftar dosen
      const dosenQuery = query(
        collection(db, "users"),
        where("role", "==", "dosen"),
        where("verified", "==", true)
      );
      const dosenSnapshot = await getDocs(dosenQuery);

      const dosenData = [];
      dosenSnapshot.forEach(doc => {
        dosenData.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setDosenList(dosenData);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Terjadi kesalahan saat mengambil data");
    } finally {
      setLoading(false);
    }
  };

  const fetchTADetail = async (taId) => {
    try {
      const taDocRef = doc(db, "tugasAkhir", taId);
      const taDoc = await getDoc(taDocRef);
      if (taDoc.exists()) {
        const taData = taDoc.data();

        // Ambil data mahasiswa
        if (taData.mahasiswaId) {
          try {
            const mahasiswaDocRef = doc(db, "users", taData.mahasiswaId);
            const mahasiswaDoc = await getDoc(mahasiswaDocRef);
            if (mahasiswaDoc.exists()) {
              taData.mahasiswa = mahasiswaDoc.data();
            }
          } catch (error) {
            console.error("Error fetching mahasiswa data:", error);
          }
        }

        // Auto-select pembimbing as penguji if available - more defensive coding
        if (taData && Array.isArray(taData.pembimbingId) && taData.pembimbingId.length > 0) {
          console.log("Auto-selecting pembimbing as penguji:", taData.pembimbingId);
          // Add pembimbing to selected penguji - defensive approach
          setSelectedPenguji(prevPenguji => {
            const newPenguji = Array.isArray(prevPenguji) ? [...prevPenguji] : [];
            taData.pembimbingId.forEach(pembimbingId => {
              if (pembimbingId && typeof pembimbingId === 'string' && !newPenguji.includes(pembimbingId)) {
                newPenguji.push(pembimbingId);
              }
            });
            return newPenguji;
          });
        }

        setSelectedTA(taData);
      }
    } catch (error) {
      console.error("Error fetching TA detail:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePengujiChange = (e) => {
    const value = e.target.value;
    const checked = e.target.checked;

    if (checked) {
      setSelectedPenguji([...selectedPenguji, value]);
    } else {
      setSelectedPenguji(selectedPenguji.filter(id => id !== value));
    }
  };

  const handleKriteriaChange = (index, field, value) => {
    const updatedKriteria = [...formData.kriteriaPenilaian];
    updatedKriteria[index][field] = field === 'bobot' ? parseInt(value) : value;

    setFormData({
      ...formData,
      kriteriaPenilaian: updatedKriteria
    });
  };

  const addKriteria = () => {
    setFormData({
      ...formData,
      kriteriaPenilaian: [
        ...formData.kriteriaPenilaian,
        { id: uuidv4(), nama: '', bobot: 0 }
      ]
    });
  };

  const removeKriteria = (index) => {
    const updatedKriteria = [...formData.kriteriaPenilaian];
    updatedKriteria.splice(index, 1);

    setFormData({
      ...formData,
      kriteriaPenilaian: updatedKriteria
    });
  };

  const validateForm = () => {
    if (!formData.tugasAkhirId) return "Pilih tugas akhir";
    if (!formData.tanggal) return "Pilih tanggal";
    if (!formData.waktu) return "Masukkan waktu";
    if (!formData.tempat) return "Masukkan tempat";
    if (selectedPenguji.length < 4) return "Pilih 4 dosen penguji (2 pembimbing dan 2 penguji utama)";

    // Validasi kriteria penilaian
    let totalBobot = 0;
    for (const kriteria of formData.kriteriaPenilaian) {
      if (!kriteria.nama) return "Nama kriteria tidak boleh kosong";
      if (kriteria.bobot <= 0) return "Bobot kriteria harus lebih dari 0";
      totalBobot += kriteria.bobot;
    }

    if (totalBobot !== 100) return "Total bobot kriteria harus 100%";

    return null;
  };

  const handleSubmit = async () => {
    try {
      const validationError = validateForm();
      if (validationError) {
        setError(validationError);
        return;
      }

      setLoading(true);

      // Ambil nama penguji
      const pengujiNama = [];
      for (const pengujiId of selectedPenguji) {
        const dosenData = dosenList.find(dosen => dosen.id === pengujiId);
        if (dosenData) {
          pengujiNama.push(dosenData.nama);
        }
      }

      // Data yang akan disimpan
      const seminarData = {
        jenis: formData.jenis,
        tugasAkhirId: formData.tugasAkhirId,
        mahasiswaId: selectedTA?.mahasiswaId,
        mahasiswaNama: selectedTA?.mahasiswa?.nama,
        mahasiswaNIM: selectedTA?.mahasiswa?.nim,
        judulTA: selectedTA?.judul,
        tanggal: formData.tanggal,
        waktu: formData.waktu,
        tempat: formData.tempat,
        pengujiId: selectedPenguji,
        pengujiNama: pengujiNama,
        catatan: formData.catatan,
        kriteriaPenilaian: formData.kriteriaPenilaian,
        createdAt: new Date().toISOString(),
        // Data untuk seminar ulang
        isReschedule: formData.isReschedule,
        previousSeminarId: formData.previousSeminarId
      };

      if (seminar) {
        // Mode edit: update dokumen yang ada
        // Preserve status jika sudah completed (jangan ubah kembali ke scheduled)
        if (seminar.status === 'completed') {
          seminarData.status = 'completed';
          // Preserve completedAt jika ada
          if (seminar.completedAt) {
            seminarData.completedAt = seminar.completedAt;
          }
        } else {
          // Jika belum completed, set status sesuai kebutuhan (bisa scheduled atau status lain)
          seminarData.status = seminar.status || 'scheduled';
        }

        await updateDoc(doc(db, "seminar", seminar.id), seminarData);
        setSuccess("Jadwal seminar berhasil diperbarui");
      } else {
        // Mode tambah: buat dokumen baru
        seminarData.status = 'scheduled';
        await addDoc(collection(db, "seminar"), seminarData);
        setSuccess("Jadwal seminar berhasil ditambahkan");
      }

      // Tutup modal setelah beberapa detik
      setTimeout(() => {
        onHide(true); // true = refresh data
      }, 2000);
    } catch (error) {
      console.error("Error saving seminar:", error);
      setError("Terjadi kesalahan saat menyimpan data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={() => onHide(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {seminar ? 'Edit' : 'Tambah'} Jadwal {formData.jenis === 'proposal' ? 'Seminar Proposal' :
            formData.jenis === 'hasil' ? 'Seminar Hasil' : 'Sidang Komprehensif'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Tampilkan informasi untuk seminar yang dijadwalkan ulang */}
        {formData.isReschedule && (
          <Alert variant="warning" className="mb-3">
            <strong>Perhatian!</strong> Ini adalah penjadwalan ulang seminar karena mahasiswa tidak lulus pada seminar sebelumnya (nilai &lt; 60).
          </Alert>
        )}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Jenis</Form.Label>
            <Form.Select
              name="jenis"
              value={formData.jenis}
              onChange={handleInputChange}
            >
              <option value="proposal">Seminar Proposal</option>
              <option value="hasil">Seminar Hasil</option>
              <option value="kompre">Sidang Komprehensif</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Tugas Akhir</Form.Label>
            <Form.Select
              name="tugasAkhirId"
              value={formData.tugasAkhirId}
              onChange={handleInputChange}
            >
              <option value="">-- Pilih Tugas Akhir --</option>
              {tugasAkhirList.map(ta => (
                <option key={ta.id} value={ta.id}>
                  {ta.mahasiswa?.nama} ({ta.mahasiswa?.nim}) - {ta.judul}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {selectedTA && (
            <Alert variant="info" className="mb-3">
              <strong>Mahasiswa:</strong> {selectedTA.mahasiswa?.nama} ({selectedTA.mahasiswa?.nim})
              <br />
              <strong>Judul:</strong> {selectedTA.judul}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tanggal</Form.Label>
                <Form.Control
                  type="date"
                  name="tanggal"
                  value={formData.tanggal}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Waktu</Form.Label>
                <Form.Control
                  type="time"
                  name="waktu"
                  value={formData.waktu}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Tempat</Form.Label>
            <Form.Control
              type="text"
              name="tempat"
              value={formData.tempat}
              onChange={handleInputChange}
              placeholder="Masukkan lokasi seminar/sidang"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Dosen Penguji</Form.Label>
            <div className="border p-3 rounded">
              <Alert variant="info" className="mb-3">
                <strong>Informasi Pembobotan Penguji:</strong>
                <ul className="mb-0">
                  <li>Pembimbing 1: 35%</li>
                  <li>Pembimbing 2: 25%</li>
                  <li>Penguji 1: 20%</li>
                  <li>Penguji 2: 20%</li>
                </ul>
                <div className="mt-2 mb-0">
                  <small>Pembimbing akan otomatis ditambahkan sebagai penguji</small>
                </div>
              </Alert>

              <Table bordered>
                <thead>
                  <tr className="table-light">
                    <th>Peran</th>
                    <th>Dosen</th>
                    <th>Bobot</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Pembimbing 1 */}
                  <tr>
                    <td><strong>Pembimbing 1</strong></td>
                    <td>
                      {selectedTA && Array.isArray(selectedTA.pembimbingId) && selectedTA.pembimbingId.length > 0 ? (
                        dosenList.find(d => d.id === selectedTA.pembimbingId[0])?.nama || 'Belum dipilih'
                      ) : 'Belum dipilih'}
                    </td>
                    <td>35%</td>
                  </tr>

                  {/* Pembimbing 2 */}
                  <tr>
                    <td><strong>Pembimbing 2</strong></td>
                    <td>
                      {selectedTA && Array.isArray(selectedTA.pembimbingId) && selectedTA.pembimbingId.length > 1 ? (
                        dosenList.find(d => d.id === selectedTA.pembimbingId[1])?.nama || 'Belum dipilih'
                      ) : 'Belum dipilih'}
                    </td>
                    <td>25%</td>
                  </tr>

                  {/* Penguji 1 */}
                  <tr>
                    <td><strong>Penguji 1</strong></td>
                    <td>
                      <Form.Select
                        value={
                          // Find an ID that is in selectedPenguji but not in pembimbingId
                          selectedPenguji.find(id =>
                            !(selectedTA &&
                              Array.isArray(selectedTA.pembimbingId) &&
                              selectedTA.pembimbingId.includes(id))
                          ) || ''
                        }
                        onChange={(e) => {
                          const newId = e.target.value;
                          // Replace the first non-pembimbing ID or add it
                          setSelectedPenguji(prev => {
                            const pembimbingIds = selectedTA && Array.isArray(selectedTA.pembimbingId) ?
                              selectedTA.pembimbingId : [];

                            // Filter out any existing external penguji
                            const filtered = prev.filter(id =>
                              pembimbingIds.includes(id) || id === newId
                            );

                            // Add new ID if it's not empty and not already in the filtered list
                            if (newId && !filtered.includes(newId)) {
                              filtered.push(newId);
                            }

                            return filtered;
                          });
                        }}
                      >
                        <option value="">-- Pilih Penguji 1 --</option>
                        {dosenList
                          .filter(dosen =>
                            !(selectedTA &&
                              Array.isArray(selectedTA.pembimbingId) &&
                              selectedTA.pembimbingId.includes(dosen.id))
                          )
                          .map(dosen => (
                            <option key={dosen.id} value={dosen.id}>
                              {dosen.nama} {dosen.nip ? `(${dosen.nip})` : ''}
                            </option>
                          ))
                        }
                      </Form.Select>
                    </td>
                    <td>20%</td>
                  </tr>

                  {/* Penguji 2 */}
                  <tr>
                    <td><strong>Penguji 2</strong></td>
                    <td>
                      <Form.Select
                        value={
                          // Find a second ID that is in selectedPenguji but not in pembimbingId
                          selectedPenguji.filter(id =>
                            !(selectedTA &&
                              Array.isArray(selectedTA.pembimbingId) &&
                              selectedTA.pembimbingId.includes(id))
                          )[1] || ''
                        }
                        onChange={(e) => {
                          const newId = e.target.value;
                          // Handle the second external penguji
                          setSelectedPenguji(prev => {
                            const pembimbingIds = selectedTA && Array.isArray(selectedTA.pembimbingId) ?
                              selectedTA.pembimbingId : [];

                            // Keep pembimbing IDs
                            const pembimbingSelected = prev.filter(id => pembimbingIds.includes(id));

                            // Find external penguji (not in pembimbingIds)
                            const externalSelected = prev.filter(id => !pembimbingIds.includes(id));

                            // Keep the first external if it exists and isn't the new one
                            if (externalSelected.length > 0 && externalSelected[0] !== newId) {
                              pembimbingSelected.push(externalSelected[0]);
                            }

                            // Add the new id if not empty
                            if (newId) {
                              pembimbingSelected.push(newId);
                            }

                            return pembimbingSelected;
                          });
                        }}
                      >
                        <option value="">-- Pilih Penguji 2 --</option>
                        {dosenList
                          .filter(dosen => {
                            // Not a pembimbing
                            const notPembimbing = !(selectedTA &&
                              Array.isArray(selectedTA.pembimbingId) &&
                              selectedTA.pembimbingId.includes(dosen.id));

                            // Not already selected as Penguji 1
                            const notPenguji1 = !(
                              selectedPenguji.find(id =>
                                !(selectedTA &&
                                  Array.isArray(selectedTA.pembimbingId) &&
                                  selectedTA.pembimbingId.includes(id))
                              ) === dosen.id
                            );

                            return notPembimbing && notPenguji1;
                          })
                          .map(dosen => (
                            <option key={dosen.id} value={dosen.id}>
                              {dosen.nama} {dosen.nip ? `(${dosen.nip})` : ''}
                            </option>
                          ))
                        }
                      </Form.Select>
                    </td>
                    <td>20%</td>
                  </tr>
                </tbody>
              </Table>
            </div>
            <Form.Text className="text-muted">
              Pastikan keempat penguji telah dipilih (2 pembimbing akan otomatis dipilih)
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Catatan (opsional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="catatan"
              value={formData.catatan}
              onChange={handleInputChange}
              placeholder="Tambahkan catatan jika diperlukan"
            />
          </Form.Group>

          <hr />

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6>Kriteria Penilaian</h6>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={addKriteria}
            >
              Tambah Kriteria
            </Button>
          </div>

          {formData.kriteriaPenilaian.map((kriteria, index) => (
            <Row key={index} className="mb-2 align-items-center">
              <Col md={6}>
                <Form.Control
                  type="text"
                  value={kriteria.nama}
                  onChange={(e) => handleKriteriaChange(index, 'nama', e.target.value)}
                  placeholder="Nama kriteria"
                />
              </Col>
              <Col md={4}>
                <Form.Control
                  type="number"
                  value={kriteria.bobot}
                  onChange={(e) => handleKriteriaChange(index, 'bobot', e.target.value)}
                  placeholder="Bobot (%)"
                  min="0"
                  max="100"
                />
              </Col>
              <Col md={2}>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => removeKriteria(index)}
                  disabled={formData.kriteriaPenilaian.length <= 1}
                >
                  Hapus
                </Button>
              </Col>
            </Row>
          ))}

          <div className="mt-2 text-end">
            <small>Total bobot: {formData.kriteriaPenilaian.reduce((total, k) => total + (k.bobot || 0), 0)}%</small>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)}>
          Batal
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Menyimpan...' : 'Simpan'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SeminarForm;
